/**
 * The things the assistant can actually do, rather than explain.
 *
 * Two of them, both written by hand: create a task, and set a reminder. There
 * is no model parsing this — a handful of prefixes and a small vocabulary of
 * dates, which is enough for the way people really phrase these ("remind me to
 * call Sharma Motors tomorrow") and, more importantly, is enough for the
 * parser to *know* when it has not understood.
 *
 * Nothing here writes on its own. A parse produces a draft that the panel
 * shows back with a Create button, so a misread phrase costs a glance rather
 * than a wrong row in somebody's task board. That is the whole reason this is
 * a parser and not a guess: a guess cannot tell you it is unsure.
 */

export type Draft =
  | { kind: 'task'; title: string; dueAt: Date | null }
  | { kind: 'reminder'; title: string; remindAt: Date };

/** Openers that mean "make me a task", longest first so the specific ones win. */
const TASK_PREFIXES = [
  'create a task to',
  'create a task called',
  'create a task',
  'add a task to',
  'add a task',
  'new task',
  'make a task',
  'task:',
];

const REMINDER_PREFIXES = [
  'remind me to',
  'remind me',
  'set a reminder to',
  'set a reminder',
  'reminder to',
  'reminder:',
];

/**
 * When they said, in words.
 *
 * Deliberately shallow. "Tomorrow" and "next Friday" cover almost everything
 * anybody types into a box like this; a full date grammar would mostly add
 * ways to be confidently wrong about what "the 3rd" meant.
 */
const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function atNineAm(d: Date): Date {
  const out = new Date(d);
  out.setHours(9, 0, 0, 0);
  return out;
}

/** Pulls a date off the end of a phrase, returning the phrase without it. */
function takeWhen(text: string, now: Date): { text: string; when: Date | null } {
  const lower = text.toLowerCase();

  const strip = (phrase: string, when: Date) => ({
    text: text
      .slice(0, lower.lastIndexOf(phrase))
      .trim()
      .replace(/[,\s]+$/, ''),
    when,
  });

  if (lower.endsWith('today')) return strip('today', atNineAm(now));

  if (lower.endsWith('tomorrow')) {
    const d = new Date(now);
    d.setDate(d.getDate() + 1);
    return strip('tomorrow', atNineAm(d));
  }

  if (lower.endsWith('next week')) {
    const d = new Date(now);
    d.setDate(d.getDate() + 7);
    return strip('next week', atNineAm(d));
  }

  // "in 3 days"
  const inDays = lower.match(/\bin (\d{1,2}) days?$/);
  if (inDays?.[1]) {
    const d = new Date(now);
    d.setDate(d.getDate() + Number(inDays[1]));
    return strip(inDays[0], atNineAm(d));
  }

  // "on friday" / "next friday" / "friday"
  for (let i = 0; i < DAYS.length; i += 1) {
    const day = DAYS[i]!;
    const m = lower.match(new RegExp(`\\b((?:on |next )?${day})$`));
    if (!m?.[1]) continue;
    const d = new Date(now);
    // Always the next one, never today — "on Friday" said on a Friday means
    // the Friday coming, which is what anybody writing it down intends.
    const ahead = (i - d.getDay() + 7) % 7 || 7;
    d.setDate(d.getDate() + ahead);
    return strip(m[1], atNineAm(d));
  }

  return { text, when: null };
}

function after(text: string, prefixes: string[]): string | null {
  const lower = text.toLowerCase().trim();
  for (const p of prefixes) {
    if (lower.startsWith(p)) return text.trim().slice(p.length).trim();
  }
  return null;
}

/**
 * A command, or nothing.
 *
 * Nothing is the common case and the important one — anything not clearly an
 * instruction falls through to the search, which is where most questions
 * belong.
 */
export function parseCommand(input: string, now = new Date()): Draft | null {
  const reminderBody = after(input, REMINDER_PREFIXES);
  if (reminderBody !== null) {
    const { text, when } = takeWhen(reminderBody, now);
    const title = text.replace(/^to\s+/i, '').trim();
    if (!title) return null;
    // A reminder with no time is not a reminder. Tomorrow morning is the
    // least surprising thing to assume, and the panel shows it before saving.
    const d = new Date(now);
    d.setDate(d.getDate() + 1);
    return { kind: 'reminder', title, remindAt: when ?? atNineAm(d) };
  }

  const taskBody = after(input, TASK_PREFIXES);
  if (taskBody !== null) {
    const { text, when } = takeWhen(taskBody, now);
    const title = text.replace(/^(?:to|called)\s+/i, '').trim();
    if (!title) return null;
    return { kind: 'task', title, dueAt: when };
  }

  return null;
}
